import type { GraphQLResult } from 'aws-amplify/api'
import { useQuery } from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  getBlueprintQuery,
  getProjectBlueprintsQuery,
} from '@/services/graphql/blueprint'
import { referenceQueryPreset } from '@/services/query-presets'
import type { BlueprintInput, BlueprintResponse } from '@/types/blueprint'

const client = getAmplifyClient()

type GraphqlClient = Pick<ReturnType<typeof getAmplifyClient>, 'graphql'>

export type ProjectBlueprintSummary = BlueprintInput & {
  createdAt?: string | null
  sections?: {
    items?: Array<{ id?: string | null } | null> | null
  } | null
}

type ProjectBlueprintsResponse = {
  blueprintsByProject?: {
    items?: Array<ProjectBlueprintSummary | null> | null
  } | null
}

type ProjectBlueprintCandidate = {
  id?: string | null
  createdAt?: string | null
  sections?: {
    items?: Array<unknown> | null
  } | null
}

export const useBlueprint = (id?: string) => {
  return useQuery({
    queryKey: ['blueprint', id],
    queryFn: () => getBlueprint(id),
    enabled: !!id,
  })
}

const getBlueprint = async (id?: string) => {
  const result = (await client.graphql({
    query: getBlueprintQuery,
    variables: { id },
  })) as GraphQLResult<BlueprintResponse>
  return result.data?.getBlueprint
}

export const projectBlueprintsQueryKey = (projectId?: string) =>
  ['projectBlueprints', projectId] as const

export const useProjectBlueprints = (projectId?: string) => {
  return useQuery({
    queryKey: projectBlueprintsQueryKey(projectId),
    queryFn: () => getProjectBlueprints(projectId),
    enabled: !!projectId && projectId !== 'create',
    ...referenceQueryPreset,
  })
}

export const getProjectBlueprints = async (
  projectId?: string,
  graphqlClient: GraphqlClient = client,
): Promise<ProjectBlueprintSummary[]> => {
  if (!projectId || projectId === 'create') return []

  const result = (await graphqlClient.graphql({
    query: getProjectBlueprintsQuery,
    variables: { id: projectId },
  })) as GraphQLResult<ProjectBlueprintsResponse>

  if (result.errors?.length) {
    throw new Error(
      result.errors
        .map((error) => error.message)
        .filter(Boolean)
        .join(', ') || 'Unable to load project blueprints',
    )
  }

  return (result.data?.blueprintsByProject?.items ?? []).filter(
    (blueprint): blueprint is ProjectBlueprintSummary => Boolean(blueprint?.id),
  )
}

const getBlueprintSectionCount = (blueprint: ProjectBlueprintCandidate) =>
  Array.isArray(blueprint.sections?.items)
    ? blueprint.sections.items.filter(Boolean).length
    : 0

const parseCreatedAt = (createdAt?: string | null) => {
  if (!createdAt) return Number.MAX_SAFE_INTEGER

  const timestamp = Date.parse(createdAt)
  return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER
}

export const selectPrimaryProjectBlueprint = <
  T extends ProjectBlueprintCandidate,
>(
  blueprints?: Array<T | null | undefined> | null,
): T | undefined => {
  const candidates = (blueprints ?? []).filter(
    (blueprint): blueprint is T => typeof blueprint?.id === 'string',
  )

  return [...candidates].sort((a, b) => {
    const sectionCountDelta =
      getBlueprintSectionCount(b) - getBlueprintSectionCount(a)
    if (sectionCountDelta !== 0) return sectionCountDelta

    const createdAtDelta =
      parseCreatedAt(a.createdAt) - parseCreatedAt(b.createdAt)
    if (createdAtDelta !== 0) return createdAtDelta

    return (a.id ?? '').localeCompare(b.id ?? '')
  })[0]
}
